// Benchmark data for /photon/performance. The raw numbers are no longer
// edited here — they come from the release artifacts in
// data/photon/releases/*.json via components/photon/generated/release-data.ts
// (regenerate: pnpm photon:releases). This module re-exports the latest
// complete benchmark and the derived helpers the pages use.

import {
  BENCHMARKS,
  LATEST_BENCHMARK,
} from "./generated/release-data";
import type {
  BaselineRuntime,
  BenchModel,
  BenchRuntime,
  ColdStartComparison,
  LatencyComparison,
  LatencyPercentiles,
  ThroughputCell,
} from "./release-types";

export type {
  BaselineRuntime,
  BenchModel,
  BenchRuntime,
  ColdStartComparison,
  LatencyComparison,
  LatencyPercentiles,
  ThroughputCell,
};
export { BENCHMARKS };

// ---- Latest complete benchmark environment (pages default to this) ----
export const BENCH_ENV = LATEST_BENCHMARK.environment;
export const BENCH_DISCLOSURES = BENCH_ENV.disclosures;
export const QUALITY_CAVEAT = BENCH_ENV.qualityCaveat;

export const RUNTIME_LABELS: Record<BenchRuntime, string> = {
  photon: BENCH_ENV.runtimes.photon,
  vllm: BENCH_ENV.runtimes.vllm,
  sglang: BENCH_ENV.runtimes.sglang,
};

export const BENCH_MODELS = LATEST_BENCHMARK.models;
export const BENCH_FAMILIES = LATEST_BENCHMARK.families;

export const THROUGHPUT_CELLS = LATEST_BENCHMARK.throughput;
export const LATENCY_ROWS = LATEST_BENCHMARK.latency;
export const COLD_START_ROWS = LATEST_BENCHMARK.coldStart;
export const RESOURCE_ROWS = LATEST_BENCHMARK.resources;

/** Percentage advantage of Photon over the baseline for one cell. */
export const throughputAdvantagePct = (cell: ThroughputCell) =>
  (cell.multiple - 1) * 100;

/** Photon's throughput as a multiple of the baseline for one cell. */
export const throughputMultiple = (cell: ThroughputCell) =>
  cell.multiple;

/** Tail spread (jitter indicator): p99 − p50, derived. */
export const latencySpread = (row: LatencyPercentiles) => row.p99 - row.p50;

/** Look up one pairwise cold-start comparison. */
export const coldStartComparison = (
  modelId: string,
  runtime: BaselineRuntime,
) => {
  const row = COLD_START_ROWS.find(
    (r) => r.modelId === modelId && r.runtime === runtime,
  );
  if (!row) throw new Error(`Missing cold-start row: ${modelId}/${runtime}`);
  return row;
};

/** Look up the display record for a benchmark model id. */
export const benchModelName = (modelId: string) => {
  const model = BENCH_MODELS.find((m) => m.id === modelId);
  if (!model) throw new Error(`Unknown benchmark model: ${modelId}`);
  return model;
};
